import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Plus, Check, X, Clock, Pill, Trash2, Shield, Key,
  Lock, Eye, AlertTriangle, ClipboardList
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== OTP GATE ====================
function OtpGate({ token, onGranted }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (code.length !== 6) { toast.error('Il codice deve essere di 6 cifre'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/prescriptions/verify-otp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await res.json();
      if (res.ok && data.granted) {
        toast.success(data.message);
        onGranted(data);
      } else {
        toast.error(data.detail || 'Codice non valido');
      }
    } catch { toast.error('Errore di rete'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto text-center py-16" data-testid="otp-gate">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Lock className="w-8 h-8 text-amber-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-800 mb-2">Accesso Protetto</h2>
      <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
        Le ricette mediche contengono dati sanitari sensibili (Art. 9 GDPR).
        Per accedere, richiedi un codice di autorizzazione a un medico o receptionist della clinica.
      </p>
      <div className="flex gap-2 max-w-xs mx-auto">
        <Input
          value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="Codice 6 cifre" className="text-center text-lg font-mono tracking-widest"
          maxLength={6} data-testid="otp-input"
        />
        <Button onClick={handleVerify} disabled={loading || code.length !== 6} className="bg-sky-500 hover:bg-sky-600" data-testid="otp-verify-btn">
          <Key className="w-4 h-4 mr-1" /> Verifica
        </Button>
      </div>
      <p className="text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
        <Shield className="w-3 h-3" /> Ogni accesso viene registrato nel log di audit
      </p>
    </div>
  );
}

// ==================== STATS ONLY VIEW (Superadmin) ====================
function StatsOnlyView({ stats }) {
  return (
    <div className="max-w-lg mx-auto text-center py-12" data-testid="stats-only-view">
      <Shield className="w-10 h-10 text-sky-500 mx-auto mb-3" />
      <h2 className="text-lg font-bold text-slate-800 mb-1">Vista Aggregata</h2>
      <p className="text-xs text-slate-500 mb-6">Come superadmin non hai accesso ai dati sanitari individuali</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          <p className="text-xs text-slate-500">Totali</p>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-4">
          <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
          <p className="text-xs text-slate-500">In attesa</p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-200 p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
          <p className="text-xs text-slate-500">Approvate</p>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4">
          <p className="text-2xl font-bold text-red-600">{stats.denied}</p>
          <p className="text-xs text-slate-500">Rifiutate</p>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function Prescriptions() {
  const { token, user } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({
    patient_id: '', doctor_id: '', type: 'renewal',
    medications: [{ name: '', dosage: '', notes: '' }], notes: ''
  });
  const [denyOpen, setDenyOpen] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [filter, setFilter] = useState('all');
  const [accessLevel, setAccessLevel] = useState(null);
  const [otpInfo, setOtpInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => { checkAccessAndLoad(); }, []);

  const checkAccessAndLoad = async () => {
    try {
      // Check access level first
      const accessRes = await fetch(`${API}/api/prescriptions/access-status`, { headers: { Authorization: `Bearer ${token}` } });
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        setAccessLevel(accessData.access_level);
        setOtpInfo(accessData.otp_info);
      }
      await loadData();
    } catch {} finally { setLoading(false); }
  };

  const loadData = async () => {
    try {
      const [pRes, patRes, docRes] = await Promise.all([
        fetch(`${API}/api/prescriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/patients`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/doctors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const pData = await pRes.json();
      if (pData.stats_only) {
        setStats(pData);
        setAccessLevel('stats_only');
      } else if (pData.otp_required) {
        setAccessLevel('otp_required');
      } else if (Array.isArray(pData)) {
        setPrescriptions(pData);
      }
      if (patRes.ok) setPatients(await patRes.json());
      if (docRes.ok) setDoctors(await docRes.json());
    } catch {}
  };

  const handleOtpGranted = (data) => {
    setAccessLevel('full');
    setOtpInfo({ granted_by: data.granted_by, expires_in_minutes: data.expires_in_minutes });
    loadData();
  };

  const handleCreate = async () => {
    if (!form.patient_id || !form.medications[0]?.name) {
      toast.error('Seleziona paziente e almeno un farmaco'); return;
    }
    try {
      const res = await fetch(`${API}/api/prescriptions`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Richiesta ricetta inviata!');
        setCreateOpen(false);
        setForm({ patient_id: '', doctor_id: '', type: 'renewal', medications: [{ name: '', dosage: '', notes: '' }], notes: '' });
        loadData();
      } else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API}/api/prescriptions/${id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Ricetta approvata!'); loadData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore'); }
  };

  const handleDeny = async (id) => {
    try {
      const res = await fetch(`${API}/api/prescriptions/${id}/deny`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: denyReason })
      });
      if (res.ok) { toast.success('Ricetta rifiutata'); setDenyOpen(null); setDenyReason(''); loadData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore'); }
  };

  const handleGenerateOtp = async () => {
    try {
      const res = await fetch(`${API}/api/prescriptions/generate-otp`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedOtp(data);
        toast.success('Codice generato!');
      } else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore'); }
  };

  const loadAuditLog = async () => {
    try {
      const res = await fetch(`${API}/api/prescriptions/audit-log`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAuditLog(await res.json());
      else toast.error('Non autorizzato');
    } catch {}
  };

  const addMedication = () => setForm({ ...form, medications: [...form.medications, { name: '', dosage: '', notes: '' }] });
  const updateMedication = (idx, field, value) => { const m = [...form.medications]; m[idx] = { ...m[idx], [field]: value }; setForm({ ...form, medications: m }); };
  const removeMedication = (idx) => { if (form.medications.length <= 1) return; setForm({ ...form, medications: form.medications.filter((_, i) => i !== idx) }); };

  const filtered = filter === 'all' ? prescriptions : prescriptions.filter(p => p.status === filter);
  const canCreate = ['doctor', 'receptionist'].includes(user?.role);
  const canApprove = user?.role === 'doctor';
  const canGenerateOtp = ['doctor', 'receptionist'].includes(user?.role);
  const canViewAudit = ['doctor', 'superadmin'].includes(user?.role);

  const statusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'In attesa' },
      approved: { bg: 'bg-emerald-100 text-emerald-700', icon: Check, label: 'Approvata' },
      denied: { bg: 'bg-red-100 text-red-700', icon: X, label: 'Rifiutata' },
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
        <s.icon className="w-3 h-3" /> {s.label}
      </span>
    );
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  // Stats only for superadmin
  if (accessLevel === 'stats_only' && stats) {
    return <Layout><div className="p-4 sm:p-8"><StatsOnlyView stats={stats} /></div></Layout>;
  }

  // OTP gate for partner/admin
  if (accessLevel === 'otp_required') {
    return <Layout><div className="p-4 sm:p-8"><OtpGate token={token} onGranted={handleOtpGranted} /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl p-4 sm:p-8" data-testid="prescriptions-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Pill className="w-6 h-6 text-emerald-500" /> Ricette Mediche
            </h1>
            <p className="text-sm text-slate-500 mt-1">{prescriptions.length} richieste totali</p>
          </div>
          <div className="flex items-center gap-2">
            {/* OTP Access info for partner */}
            {otpInfo && (
              <span className="text-xs bg-sky-100 text-sky-700 px-3 py-1.5 rounded-full flex items-center gap-1" data-testid="otp-active-badge">
                <Key className="w-3 h-3" /> Autorizzato da {otpInfo.granted_by} ({otpInfo.expires_in_minutes}min)
              </span>
            )}
            {/* OTP Generation for doctor/receptionist */}
            {canGenerateOtp && (
              <Button variant="outline" size="sm" onClick={handleGenerateOtp} className="text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="generate-otp-btn">
                <Key className="w-4 h-4 mr-1" /> Genera Codice Accesso
              </Button>
            )}
            {/* Audit log for doctor */}
            {canViewAudit && (
              <Button variant="outline" size="sm" onClick={() => { setShowAuditLog(true); loadAuditLog(); }} data-testid="audit-log-btn">
                <ClipboardList className="w-4 h-4 mr-1" /> Registro Accessi
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setCreateOpen(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="new-prescription-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuova Ricetta
              </Button>
            )}
          </div>
        </div>

        {/* Redacted warning */}
        {accessLevel === 'redacted' && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700" data-testid="redacted-warning">
            <Eye className="w-4 h-4 shrink-0" />
            <span>I dettagli dei farmaci sono nascosti per la tua privacy. Solo i medici vedono i farmaci prescritti.</span>
          </div>
        )}

        {/* Generated OTP display */}
        {generatedOtp && (
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 flex items-center justify-between" data-testid="otp-display">
            <div>
              <p className="text-sm font-medium text-sky-800">Codice di Accesso Generato</p>
              <p className="text-xs text-sky-600 mt-0.5">Comunica questo codice al partner/admin. Valido per {generatedOtp.expires_in_minutes} minuti.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-mono font-bold text-sky-700 tracking-widest">{generatedOtp.code}</span>
              <Button variant="ghost" size="sm" onClick={() => setGeneratedOtp(null)}><X className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          {[{ value: 'all', label: 'Tutte' }, { value: 'pending', label: 'In attesa' }, { value: 'approved', label: 'Approvate' }, { value: 'denied', label: 'Rifiutate' }].map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f.value ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`filter-${f.value}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2" />
              <p>Nessuna ricetta</p>
            </div>
          ) : filtered.map(p => (
            <div key={p.id} className={`bg-white border rounded-xl p-4 ${p.redacted ? 'border-amber-200' : 'border-slate-200'}`} data-testid={`prescription-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-semibold text-slate-800">{p.patient_name || 'Paziente'}</p>
                    {statusBadge(p.status)}
                    <span className="text-xs text-slate-400 px-2 py-0.5 bg-slate-50 rounded">{p.type === 'renewal' ? 'Rinnovo' : p.type === 'chronic' ? 'Cronico' : 'Nuova'}</span>
                    {p.redacted && <span className="text-xs text-amber-600 px-2 py-0.5 bg-amber-50 rounded flex items-center gap-1"><Lock className="w-3 h-3" /> Riservato</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(p.medications || []).map((m, i) => (
                      <span key={i} className={`text-xs px-2 py-1 rounded-lg ${p.redacted ? 'bg-amber-50 text-amber-500 italic' : 'bg-blue-50 text-blue-700'}`}>
                        {m.name}{m.dosage ? ` - ${m.dosage}` : ''}
                      </span>
                    ))}
                  </div>
                  {p.notes && <p className="text-xs text-slate-500 mt-2">{p.notes}</p>}
                  <p className="text-[10px] text-slate-400 mt-2">
                    {p.created_at ? new Date(p.created_at).toLocaleString('it-IT') : ''}
                    {p.doctor_name ? ` | Dr. ${p.doctor_name}` : ''}
                  </p>
                </div>
                {p.status === 'pending' && canApprove && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => handleApprove(p.id)} data-testid={`approve-${p.id}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => setDenyOpen(p.id)} data-testid={`deny-${p.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Nuova Richiesta Ricetta</DialogTitle>
              <DialogDescription>Compila i dati per richiedere una ricetta medica.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Paziente *</Label>
                <select value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="prescription-patient">
                  <option value="">Seleziona paziente</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Medico</Label>
                <select value={form.doctor_id} onChange={e => setForm({...form, doctor_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="prescription-doctor">
                  <option value="">Seleziona medico</option>
                  {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="renewal">Rinnovo</option>
                  <option value="new">Nuova</option>
                  <option value="chronic">Farmaco cronico</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Farmaci *</Label>
                  <button onClick={addMedication} className="text-xs text-sky-500 hover:text-sky-600">+ Aggiungi farmaco</button>
                </div>
                {form.medications.map((m, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input placeholder="Nome farmaco" value={m.name} onChange={e => updateMedication(i, 'name', e.target.value)} className="flex-1" />
                    <Input placeholder="Posologia" value={m.dosage} onChange={e => updateMedication(i, 'dosage', e.target.value)} className="w-32" />
                    {form.medications.length > 1 && <button onClick={() => removeMedication(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
              </div>
              <div>
                <Label>Note</Label>
                <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Note aggiuntive..." className="mt-1" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCreateOpen(false)}>Annulla</Button>
                <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={handleCreate} data-testid="submit-prescription">Invia Richiesta</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Deny Dialog */}
        <Dialog open={!!denyOpen} onOpenChange={() => setDenyOpen(null)}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Motivo del Rifiuto</DialogTitle>
              <DialogDescription>Specifica il motivo per cui la ricetta viene rifiutata.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Input value={denyReason} onChange={e => setDenyReason(e.target.value)} placeholder="Es: Necessaria visita di controllo" />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDenyOpen(null)}>Annulla</Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={() => handleDeny(denyOpen)}>Conferma Rifiuto</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Audit Log Dialog */}
        <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Registro Accessi Ricette</DialogTitle>
              <DialogDescription>Log GDPR di tutti gli accessi ai dati delle ricette mediche.</DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {auditLog.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Nessun accesso registrato</p>
              ) : auditLog.map((log, i) => (
                <div key={i} className="text-xs bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-700">{log.user_name} <span className="text-slate-400 font-normal">({log.user_role})</span></span>
                    <span className="text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString('it-IT') : ''}</span>
                  </div>
                  <p className="text-slate-600">
                    <span className={`font-medium ${log.action.includes('blocked') || log.action.includes('failed') ? 'text-red-600' : log.action.includes('approved') ? 'text-emerald-600' : 'text-sky-600'}`}>
                      {log.action}
                    </span>
                    {log.details && <span className="ml-1 text-slate-500">— {log.details}</span>}
                  </p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
